/**
 * ConfirmPopover — a tiny shared "are you sure?" popover anchored to its own
 * trigger (Task 14 / C6). Replaces jarring, Tauri-flaky `window.confirm` for
 * destructive actions with an in-app confirm that can carry DIFFERENTIATED copy
 * and styling per action (e.g. a neutral local "remove from device" vs. a danger
 * account-wide "delete everywhere").
 *
 * No modal library and no portal: the trigger + popover live inside one
 * relatively-positioned wrapper. The popover itself is `position: fixed` and
 * MEASURED against that wrapper (WS-Z1 fix round 2, O-1) — absolute positioning
 * put it inside `.table-scroll`, whose `overflow-x: auto` clips the other axis
 * too, and the last row's confirm on `#editor` was cut away entirely. It closes
 * on Escape or an outside click (→ cancel), and only ONE is ever open (fix round 3, L-4).
 * Focus moves to Cancel on open and back to the trigger on a cancelling close — the contract
 * `ui/Modal.tsx` and the template preview dialog already keep (PR #61 review, F3).
 *
 * `fixed` only escapes while no ANCESTOR is a fixed containing block, so in DEV the component
 * walks its own chain and warns when one is (fix round 3, L-3).
 *
 * Usage — the child render-prop receives `toggle` (open/close) and `isOpen` so
 * the existing trigger button keeps its own testid / disabled state:
 *
 *   <ConfirmPopover message="…" confirmLabel="Delete everywhere" danger
 *                   onConfirm={() => void doDelete()}>
 *     {(toggle) => <button onClick={toggle} data-testid="template-delete">Delete</button>}
 *   </ConfirmPopover>
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./ui.css";

/** The gap between the trigger and the popover — `--space-1`, the value `ui.css` used to
 *  spend on `top: calc(100% + var(--space-1))`. */
const GAP = 4;

/**
 * The first ancestor that is a FIXED CONTAINING BLOCK, or `null` (WS-Z1 fix round 3, L-3).
 *
 * `position: fixed` is the one position no ancestor's `overflow` can clip — but only while no
 * ancestor carries `transform`, `filter`, `perspective`, `will-change: transform` or
 * `contain: paint|layout`. Any of those re-parents the popover to that element, and the
 * coordinates this component measured against the VIEWPORT then mean something else: measured
 * live, `.shell__main { transform: translateZ(0) }` — an ordinary performance hint — wrote
 * `--confirm-pop-top: 467px` and put the popover at 372px, 95px off its anchor, silently.
 * `G-56m` cannot see it, because nothing covers the buttons.
 *
 * The chain is clean today (measured, review-3 §1.3) and `design-system.test.ts` keeps the
 * shell's, the table's and the two panels' stylesheets from declaring any of it. This is the
 * other half: the ancestor a component introduces — a `Drawer` or `Modal` panel is a fixed
 * containing block for the 160ms its opening animation runs.
 */
function fixedContainingBlock(el: HTMLElement): { node: HTMLElement; why: string } | null {
  const view = el.ownerDocument.defaultView;
  if (!view) return null;
  const idle = (v: string | null | undefined) => !v || v === "none" || v === "normal" || v === "auto";
  for (let p = el.parentElement; p; p = p.parentElement) {
    const s = view.getComputedStyle(p);
    for (const prop of ["transform", "filter", "perspective"] as const)
      if (!idle(s[prop])) return { node: p, why: `${prop}: ${s[prop]}` };
    if (/\b(transform|filter|perspective)\b/.test(s.willChange || ""))
      return { node: p, why: `will-change: ${s.willChange}` };
    if (/\b(paint|layout|strict|content)\b/.test(s.contain || ""))
      return { node: p, why: `contain: ${s.contain}` };
  }
  return null;
}

/**
 * THE ONE OPEN POPOVER (WS-Z1 fix round 3, L-4).
 *
 * `open` is per-instance and the outside-close listener is a `mousedown`, so a keyboard user
 * could open two destructive confirms at once: activating a button with Enter fires `click`
 * and no `mousedown`, and the first popover never heard the outside event. Measured live, the
 * first one's `confirm-accept` centre was then answered by the OTHER popover — the one
 * reachable path on which "both buttons answer" was not true.
 *
 * A module-level registry rather than a context, because the two call sites are in different
 * trees (a table row and the Sessions panel) and nothing composes them. Each instance
 * registers a stable handle while it is open and closes every other one first; the handle is
 * removed on close AND on unmount, so a popover that is torn down while open cannot close the
 * next one.
 */
const OPEN_POPOVERS = new Set<{ close: () => void }>();

/** `DIV.shell__main` — enough to find the element in the tree, no more. */
function nameOf(el: HTMLElement): string {
  const cls = typeof el.className === "string" && el.className.trim()
    ? "." + el.className.trim().split(/\s+/).join(".")
    : "";
  return `${el.tagName}${cls}`;
}

export interface ConfirmPopoverProps {
  /** The question shown in the popover. */
  message: string;
  /** Label for the confirm button (e.g. "Delete everywhere", "Remove from device"). */
  confirmLabel: string;
  /** Label for the cancel button. */
  cancelLabel?: string;
  /** Danger styling on the confirm button (destructive, account-wide actions). */
  danger?: boolean;
  /** Invoked when the user confirms. The popover closes first. */
  onConfirm: () => void;
  /** Render the trigger. `toggle` opens/closes the popover; `isOpen` reflects state. */
  children: (toggle: () => void, isOpen: boolean) => React.ReactNode;
  className?: string;
}

export function ConfirmPopover({
  message,
  confirmLabel,
  cancelLabel = "Cancel",
  danger = false,
  onConfirm,
  children,
  className,
}: ConfirmPopoverProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLSpanElement>(null);
  const warnedCB = useRef(false);

  const cancelRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setOpen(false), []);
  /**
   * WHERE FOCUS GOES (PR #61 review, F3). The app already has a dialog contract and this is
   * it: `ui/Modal.tsx` focuses its Close button on open, and the template preview dialog
   * focuses Close on open and hands focus back to the opener on close (review-1 F-1/F-2). This
   * popover is a `role="dialog"` too, so it focuses the DISMISSIVE control — Cancel — on open
   * and returns focus to its trigger on every CANCELLING close. It used to leave focus on
   * `<body>` after Escape, measured.
   *
   * Confirm is the one close that does not restore: the action usually removes the row the
   * trigger lives on, and chasing a disappearing button is worse than letting the page take
   * focus back. Nor does the close that L-4's registry performs — that popover was abandoned
   * because focus went somewhere else.
   */
  const closeAndRestore = useCallback(() => {
    const wrap = wrapRef.current;
    const active = wrap?.ownerDocument.activeElement;
    setOpen(false);
    if (!wrap || !active || !wrap.contains(active)) return;
    for (const el of wrap.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ))
      if (!popRef.current || !popRef.current.contains(el)) {
        el.focus();
        return;
      }
  }, []);
  const toggle = useCallback(() => setOpen((o) => !o), []);
  /** This instance's entry in `OPEN_POPOVERS` — one object for the component's whole life. */
  const selfRef = useRef<{ close: () => void }>(undefined as unknown as { close: () => void });
  if (!selfRef.current) selfRef.current = { close };

  // ONE OPEN POPOVER AT A TIME (L-4). Opening closes every other one, including the ones a
  // keyboard opened without ever firing a `mousedown` for the outside-close listener to hear.
  useEffect(() => {
    if (!open) return;
    const self = selfRef.current;
    for (const other of [...OPEN_POPOVERS]) if (other !== self) other.close();
    OPEN_POPOVERS.add(self);
    // …and focus lands on Cancel, after the other popovers have closed so none of them can
    // take it back. F3: the dialog contract `Modal` and the preview dialog already keep.
    cancelRef.current?.focus();
    return () => {
      OPEN_POPOVERS.delete(self);
    };
  }, [open]);

  // Escape closes (cancels); an outside mousedown closes too. Listeners are only
  // attached while open so they never leak or intercept clicks otherwise.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeAndRestore();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) closeAndRestore();
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onDown, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onDown, true);
    };
  }, [open, closeAndRestore]);

  /**
   * WHERE THE POPOVER GOES (WS-Z1 fix round 2, O-1).
   *
   * `position: fixed` is the one position an ancestor's `overflow` cannot clip, and the price
   * of it is that the popover no longer knows where its trigger is. So it is measured: below
   * the anchor when it fits under it, above it when it does not — the last row of a table is
   * exactly the case where it does not — and right-aligned to the anchor either way.
   *
   * `useLayoutEffect` so the coordinates are written BEFORE paint (no visible jump), and the
   * same measurement re-runs on scroll (capture, so an inner scroller counts) and on resize:
   * a fixed popover that stayed put while its row scrolled away would point at nothing.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const anchor = wrapRef.current;
      const pop = popRef.current;
      if (!anchor || !pop) return;
      const a = anchor.getBoundingClientRect();
      const p = pop.getBoundingClientRect();
      const below = a.bottom + GAP;
      const top = below + p.height <= window.innerHeight - GAP
        ? below
        : Math.max(GAP, a.top - GAP - p.height);
      pop.style.setProperty("--confirm-pop-top", `${Math.round(top)}px`);
      pop.style.setProperty("--confirm-pop-right", `${Math.round(Math.max(GAP, window.innerWidth - a.right))}px`);
    };
    place();
    // `import.meta.env.DEV` is read INSIDE the effect and WRAPS the whole body, which is the
    // shape the bundler deletes (the arrangement `ActionBar` uses, fix round 2 N-2/N-3).
    if (import.meta.env.DEV) {
      if (!warnedCB.current && wrapRef.current) {
        const cb = fixedContainingBlock(wrapRef.current);
        if (cb) {
          warnedCB.current = true;
          console.warn(
            `ConfirmPopover: ${nameOf(cb.node)} declares ${cb.why}, which makes it a FIXED ` +
              "CONTAINING BLOCK. The popover measures its anchor against the viewport, so its " +
              "coordinates are now wrong by that element's offset (measured: 95px). Remove the " +
              "property, or portal the popover to document.body.",
          );
        }
      }
    }
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  const confirm = useCallback(() => {
    setOpen(false);
    onConfirm();
  }, [onConfirm]);

  return (
    <span ref={wrapRef} className={`confirm-anchor${className ? ` ${className}` : ""}`}>
      {children(toggle, open)}
      {open && (
        <span ref={popRef} className="confirm-popover" role="dialog" aria-label={message} data-testid="confirm-popover">
          <span className="confirm-popover__msg">{message}</span>
          <span className="confirm-popover__actions">
            <button
              ref={cancelRef}
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={closeAndRestore}
              data-testid="confirm-cancel"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className={`btn btn--sm ${danger ? "btn--danger" : "btn--primary"}`}
              onClick={confirm}
              data-testid="confirm-accept"
            >
              {confirmLabel}
            </button>
          </span>
        </span>
      )}
    </span>
  );
}
